import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tag, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function ReasonSettings() {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reasons");
      setReasons(data.reasons || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load reasons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const reason = value.trim();
    if (!reason) return;
    setAdding(true);
    try {
      const { data } = await api.post("/reasons", { reason });
      setReasons(data.reasons || []);
      setValue("");
      toast.success(`Added "${reason}"`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add reason");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (reason) => {
    setRemoving(reason);
    try {
      const { data } = await api.delete(`/reasons/${encodeURIComponent(reason)}`);
      setReasons(data.reasons || []);
      toast.success(`Removed "${reason}"`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove reason");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="border border-[#e6e5df] bg-white rounded-sm" data-testid="reason-settings">
      <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
        <Tag size={16} className="text-[#647265]" />
        <div className="font-display font-bold text-[#232826]">Rework Reasons</div>
        <div className="ml-auto text-xs font-mono text-[#6b6a68]">{reasons.length} reason{reasons.length !== 1 ? "s" : ""}</div>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-[#6b6a68]">These appear in the "Rework reason" dropdown on the New Request form. Removing a reason won't change past records.</p>

        <div className="flex flex-wrap gap-2" data-testid="reason-list">
          {loading && <div className="font-mono text-xs text-[#6b6a68]">Loading…</div>}
          {!loading && reasons.map((r) => (
            <span key={r} data-testid={`reason-chip-${r}`}
              className="inline-flex items-center gap-1.5 rounded-sm border border-[#cfcfc9] bg-[#efece5] px-2.5 py-1 text-sm text-[#232826]">
              {r}
              <button
                type="button"
                onClick={() => remove(r)}
                disabled={removing === r || reasons.length <= 1}
                title={reasons.length <= 1 ? "At least one reason must remain" : `Remove ${r}`}
                data-testid={`reason-remove-${r}`}
                className="text-[#8a3a2a] hover:text-[#6a2a1e] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>

        <form onSubmit={add} className="flex gap-2 pt-1">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Add a new reason…"
            data-testid="reason-input"
            className="bg-white border-[#cfcfc9] rounded-sm text-[#232826] max-w-xs"
          />
          <Button
            type="submit"
            disabled={adding || !value.trim()}
            data-testid="reason-add-btn"
            className="bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase text-xs tracking-widest"
          >
            <Plus size={14} className="mr-1.5" /> {adding ? "Adding…" : "Add"}
          </Button>
        </form>
      </div>
    </div>
  );
}
